import Button from 'tio-ui/components/button';
import TooltipButton from 'tio-ui/components/tooltip-button';
import { dropTask } from 'ember-concurrency';
import { Listbox } from 'tio-ui/components/listbox';
import Modal from 'tio-ui/components/modal';
import Footer from 'tio-ui/components/footer';
import { not, or, and } from 'tio-ui/utilities';
import {
  PhSparkle,
  PhChatCenteredText,
  PhBuilding,
  PhCurrencyCircleDollar,
  PhFile,
  PhCalendarDots,
  PhStudent,
  PhDotsThree,
  PhXCircle,
  PhArrowUUpLeft,
  PhHandDeposit,
  PhUserSwitch,
  PhClockCounterClockwise,
  PhCreditCard,
  PhShieldWarning,
  PhTag,
} from 'ember-phosphor-icons';
import type IntlService from 'ember-intl/services/intl';
import type SessionContextService from '../../../services/session-context.ts';
import Popover from 'tio-ui/components/popover';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Textarea from 'tio-ui/components/textarea';
import { cached } from '@glimmer/tracking';
import { tracked } from '@glimmer/tracking';
import AlertBanner from 'tio-ui/components/alert-banner';
import ApproveModal from './approve-modal.gts';
import Component from '@glimmer/component';
import SelectTextArea from '../../tio/select-text-area.gts';
import ErrorMessages from 'tio-ui/components/error-messages';
import {
  buildTemplateResponseOptions,
  getSingleValueForTasField,
  setTrackedTasFieldValue,
  type TemplateFieldName,
} from '../../../utils/tuition-assistance/fields.ts';
import { setSemesterCodeString } from '../../../utils/format.ts';
import {
  reloadRecord,
  saveRecord,
  saveExistingRecordWithChangelog,
} from 'tio-data/requests/save-record';
import { holdTasParticipantRequest } from 'tio-data/requests/tas-participant-actions';
import { concat } from '@ember/helper';
import Checkbox from 'tio-ui/components/checkbox';
import Input from 'tio-ui/components/input';
import SelectableCard from 'tio-ui/components/selectable-card';
import inputmask from '../../../modifiers/inputmask.ts';
import { formatAsDollars } from '../../../utils/format.ts';
import { getInputmaskUnmaskedValue } from '../../../utils/inputmask.ts';
import { rejectHoldNote } from '../../../utils/reject-hold-note.ts';
import parseApiError from '../../../utils/parse-api-error.ts';
import {
  buildTasTransitionFieldData,
  declineCoursesRequest,
  declineEvidenceRequest,
  loadTasApplicationHistory,
  rescindLastApproverActionRequest,
  requestRepaymentRequest,
  toggleTasApplicationInCollections,
  transitionTasApplicationRequest,
  updateApprovedTotalRequest,
  TAS_TRANSITION_EVENT_FOR_METHOD,
} from 'tio-data/requests/tas-application-actions';
import {
  requestChangesRequest as declineProgramRequest,
  requestProgramCompletionChangesRequest,
  requestProgramWithdrawalChangesRequest,
  rescindLastApproverActionRequest as rescindTasProgramInstanceRequest,
} from 'tio-data/requests/tas-program-instance';
import { buildTasActorFromSession } from 'tio-data/utils/tas-actor';
import type {
  ApplicationApprovalSummary,
  ApplicationApprovalSummaryApprover,
} from '../../../types/tuition-assistance';
import type FeaturesService from '../../../services/features';
import type TuitionAssistanceService from '../../../services/tuition-assistance.ts';
// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { RoleViewExtension as RoleView } from 'tio-data/data/resources/role-view.ext';

/** System/internal fields that should not be validated as user-fillable */
const SYSTEM_FIELDS = new Set([
  'APPLICATION_ID',
  'PRE_APPROVED_TOTAL',
  'FINAL_APPROVED_TOTAL',
  'CARD_PRE_APPROVED_TOTAL',
  'CARD_FINAL_APPROVED_TOTAL',
  'APPROVED_COURSE_TOTAL',
  'WAIVE_EVIDENCE_ATTACHMENT_REQUIREMENT',
  'WAIVE_EVIDENCE_APPROVAL_REQUIREMENT',
  'EVIDENCE_APPROVAL_WAIVED_BY',
  'SCHOLARSHIPS_RECEIVED',
  'CLAIMS_FINANCIALS',
  'GRADE_POINT_AVERAGE',
  'PAYMENT_TYPE_FOR_DIRECT_PAY',
  // Template-level config fields (PROGRAM province, not employee-fillable)
  'TYPE',
  'PROGRAM_CODE',
  'PROGRAM_NAME',
  'PROGRAM_TYPE',
  'PROGRAM_DESCRIPTION',
  'PAYROLL_EXPECTATION',
  'CREATE_SIGNATURE_FLOW',
  'CLOSE_PROGRAM_BASED_ON',
  'INSTITUTION_LIST_SUPPLIED',
  'INSTITUTION_LIST_IS_EXCLUSIVE',
  'MAXIMUM_ANNUAL_REIMBURSEMENT',
  'MAXIMUM_LIFETIME_REIMBURSEMENT',
  'MAXIMUM_ANNUAL_REIMBURSEMENT_BY_EMPLOYMENT_TYPE',
  'ALLOW_UNACCREDITED_INSTITUTIONS',
  'ALLOW_PRE_APPROVAL_SUBMISSION_BEYOND_LIMIT',
  'ALLOW_WAIVER_OF_EVIDENCE_APPROVAL',
  'LOCK_EMPLOYEE_STATUS_BASED_ON',
  'CALCULATE_TOWARDS_TOTAL_BASED_ON',
  'COMPLETION_DOCUMENTS_STRING',
  'COURSE_APPROVAL_DOCUMENTS_STRING',
  'COURSE_INFORMATION',
  'YOUR_INFORMATION',
  'SCHOLARSHIP_NAME',
  'SCHOLARSHIP_AMOUNT',
  // Auto-computed at submit time from COURSES_BEGIN_DATE
  'SEMESTER_CODE',
]);

/**
 * Footer action bar for TAS approval page
 * Contains Approve button, drawer navigation icons, and more actions menu
 *
 * Layout:
 * - Desktop: Horizontal row with all buttons
 * - Mobile: Stacked layout (Approve button on top, icon grid below)
 */
interface ApprovalFooterSignature {
  Element: HTMLDivElement;
  Args: {
    /** Callback when drawer should open */
    onDrawerOpen: (drawerType: string) => void;
    /** Currently active drawer type (for visual feedback) */
    activeDrawer?: string | null;
    /** Whether approve button should show loading state */
    approveButtonLoading?: boolean;
    /** TAS Application model - needed for approval workflows */
    application: TasApplicationModel;
    /** Consuming app context - optional, will be computed from FeaturesService */
    consumingApp?: string;
    /** Whether the application is currently being edited (disables approve button) */
    isEditingApplication?: boolean;
    /** Query param to enable AI analysis in production (tio-admin only) */
    ai?: boolean;
    /** Dynamic expense type label (e.g., "Courses", "Course Cash") */
    expenseType?: string;
    /** Optional callback to open the CourseCash drawer (admin-only) */
    onCourseCashOpen?: () => void;
  };
}

class ApprovalFooter extends Component<ApprovalFooterSignature> {
  @service declare store: StoreService;
  @service declare features: FeaturesService;
  @service declare intl: IntlService;
  @service declare tuitionAssistance: TuitionAssistanceService;
  @service declare sessionContext: SessionContextService;

  @tracked isRejectModalOpen = false;
  @tracked rejectNote = '';
  @tracked rejectError: string | null = null;

  // Reject-time repayment. Both consequences are the approver's decision, defaulted
  // on so the common case (recover what was spent, hold the employee) stays one
  // click — mirroring the Request Repayment modal, which also pre-selects the full
  // amount and pre-checks the hold. Seeded in openRejectModal, not the constructor:
  // `amounts` can still be loading when the footer is built, and a stale 0 here
  // would send `request_repayment` an amount it rejects outright.
  @tracked requestRepaymentOnReject = false;
  @tracked holdEmployeeOnReject = false;
  @tracked requestedRepaymentAmount = 0;
  @tracked requestedRepaymentAmountDisplay = '';
  @tracked repaymentAmountSelection = 'full';

  // Approve modal state
  @tracked isApproveModalOpen = false;
  @tracked approveCommentDraft = '';

  // Submit for User state
  @tracked submitForUserModalOpen = false;
  @tracked submitForUserError: string | null = null;

  // Rescind state
  @tracked isRescindModalOpen = false;
  @tracked rescindError: string | null = null;

  // Request Change state
  @tracked isRequestChangeModalOpen = false;
  @tracked requestChangeNote = '';
  @tracked requestChangeError: string | null = null;

  // Reassign state
  @tracked selectedApproverToReassign: ApplicationApprovalSummaryApprover | null = null;
  @tracked isReassignModalOpen = false;

  // Reopen state
  @tracked isReopenModalOpen = false;
  @tracked reopenError: string | null = null;

  // Policy Override state
  @tracked isOverrideModalOpen = false;

  // In Collections state
  @tracked isInCollectionsModalOpen = false;
  @tracked inCollectionsError: string | null = null;

  // Popover state - control the more actions popover
  @tracked isMoreActionsPopoverOpen = false;

  /**
   * Determines if AI analysis feature should be shown
   * In tio-admin:
   * - Non-production: Enabled with feature flag
   * - Production: Enabled with feature flag AND ?ai=true query param
   */
  /** Whether to show the CourseCash speed dial icon */
  get showCourseCash() {
    return (
      this.args.application.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH' &&
      !!this.args.onCourseCashOpen
    );
  }

  get showAiAnalysis(): boolean {
    if (!this.features.isAdminApp) {
      return false;
    }

    const isProduction = this.features.tioEnvironment === 'production';
    const hasAiFeatureFlag = this.features.isEnabled('aiAnalysis');
    const hasAiQueryParam = this.args.ai === true;

    // In production, require both feature flag and query param
    if (isProduction) {
      return hasAiFeatureFlag && hasAiQueryParam;
    }

    // In non-production, just need feature flag
    return hasAiFeatureFlag;
  }

  /**
   * Compute consuming app from FeaturesService or use passed-in value
   * Maps modulePrefix to consumingApp identifier
   */
  get computedConsumingApp(): string {
    // If consumingApp is explicitly passed, use it
    if (this.args.consumingApp) {
      return this.args.consumingApp;
    }

    // Otherwise, derive from FeaturesService
    if (this.features.isAdminApp) {
      return 'tio-admin';
    } else if (this.features.isEmployeeApp) {
      return 'tio-employee';
    } else if (this.features.isAgentApp) {
      return 'tio-agent';
    }

    return 'unknown';
  }

  /**
   * Get the current approval queue based on application state
   * Focuses on TasApplication approval states
   */
  get approvalQueue(): ApplicationApprovalSummary {
    const { application } = this.args;

    if (application.state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL') {
      return application.evidenceApproval;
    } else if (application.state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL') {
      return application.coursesApproval;
    } else {
      return {
        affirmed: [],
        current_reviewers: [],
        possible_reviewers: [],
        required: [],
        approved: false,
      };
    }
  }

  /**
   * Determines if reassign approver action should be available
   * Shows when application is in a state with a pending approval queue
   */
  get canReassignApprover(): boolean {
    const approvalQueue = this.approvalQueue;
    // Show if there are possible reviewers to reassign
    return approvalQueue.possible_reviewers?.length > 0;
  }

  /**
   * Get company ID for scoping approver search.
   *
   * The `application` arg is typed as `TasApplicationModel` on this component,
   * so we go through its `tasProgramInstance` relationship. A prior version of
   * this getter branched on `modelName === 'tas-program-instance'` but that
   * branch was unreachable under the current signature.
   */
  get companyId(): string {
    return this.args.application.tasProgramInstance.employee.company.id ?? '';
  }

  /**
   * Determines if rejection action should be enabled
   * Only enabled for applications in pending approval states
   */
  get canRejectApplication() {
    const state = this.args.application.state;
    return (
      state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL' ||
      state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL'
    );
  }

  /**
   * Determines if Request Change action should be enabled
   * Same conditions as rejection - only for pending approval states
   */
  get canRequestChange() {
    return this.canRejectApplication; // Same logic as reject
  }

  /**
   * Determines if Approve action should be enabled
   * Disabled if:
   * - Application is being edited (unsaved changes)
   * - Application is not in a pending approval state
   */
  get canApproveApplication() {
    // Disable if currently editing the application
    if (this.args.isEditingApplication) {
      return false;
    }

    const state = this.args.application.state;
    return state.includes('PENDING');
  }

  /**
   * Determines the correct adapter method for Request Change action
   * Returns method name based on application state:
   * - PENDING_COURSES_APPROVAL → 'requestChanges'
   * - PENDING_EVIDENCE_APPROVAL → 'requestCourseEvidenceChanges'
   */
  get requestChangeAdapterMethod(): string {
    const state = this.args.application.state;

    if (state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL') {
      return 'requestChanges';
    } else if (state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL') {
      return 'requestCourseEvidenceChanges';
    }

    return 'requestChanges'; // Default fallback
  }

  /**
   * Get response template field name based on approval action
   */
  private getFieldNameForAction(action: 'reject' | 'requestChange'): TemplateFieldName | null {
    const state = this.args.application.state;

    if (state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL') {
      return action === 'reject'
        ? ('EVIDENCE_APPROVAL_REJECT_TEXT' as TemplateFieldName)
        : ('EVIDENCE_APPROVAL_REVISE_TEXT' as TemplateFieldName);
    }

    if (state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL') {
      return action === 'reject'
        ? ('COURSES_APPROVAL_REJECT_TEXT' as TemplateFieldName)
        : ('COURSES_APPROVAL_REVISE_TEXT' as TemplateFieldName);
    }

    return null;
  }

  /**
   * Get the TAS response tag for the given action based on application state.
   */
  private getTagForAction(action: 'reject' | 'requestChange'): string | null {
    const state = this.args.application.state;

    if (state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL') {
      return action === 'reject' ? 'TAS.EvidenceApproval.REJECT' : 'TAS.EvidenceApproval.REVISE';
    }

    if (state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL') {
      return action === 'reject' ? 'TAS.CourseApproval.REJECT' : 'TAS.CourseApproval.REVISE';
    }

    return null;
  }

  /**
   * Get company comments filtered by a specific TAS response tag.
   */
  private companyCommentsForTag(tag: string): { title: string; body: string }[] {
    const partnerComments =
      this.args.application.tasProgramInstance?.employee?.company?.comments ?? [];
    return partnerComments
      .filter((comment) => comment.tags?.includes(tag))
      .map((comment) => comment.tasApprovalNote);
  }

  /**
   * The single source of truth for an action's comment picker: the program
   * template's response templates (multi-option TasFieldOptions) OR its single
   * inline `fields` note, merged with company canned responses tagged for the
   * action. Both the pre-fill (`template*Comment`) and the dropdown
   * (`*TextAreaOptions`) derive from this, so a lone company comment can never
   * hide the program note (see buildTemplateResponseOptions).
   */
  private combinedResponseOptions(
    action: 'reject' | 'requestChange'
  ): { title: string; body: string }[] {
    const template = this.args.application.tasProgramInstance?.tasProgramTemplate;
    const templateOptions = buildTemplateResponseOptions(
      this.getFieldNameForAction(action),
      template?.fields,
      template?.tasFieldOptions ?? [],
      this.intl.t('common.tas.approval.program_response_label')
    );
    const tag = this.getTagForAction(action);
    const companyOptions = tag ? this.companyCommentsForTag(tag) : [];

    return [...templateOptions, ...companyOptions];
  }

  /**
   * Get template text for reject comment.
   * When exactly 1 combined option is configured, pre-fill the textarea with it.
   */
  get templateRejectComment() {
    const allOptions = this.combinedResponseOptions('reject');
    return allOptions.length === 1 ? (allOptions[0]?.body ?? '') : '';
  }

  /**
   * Get template text for request-change comment.
   * When exactly 1 combined option is configured, pre-fill the textarea with it.
   */
  get templateRequestChangeComment() {
    const allOptions = this.combinedResponseOptions('requestChange');
    return allOptions.length === 1 ? (allOptions[0]?.body ?? '') : '';
  }

  /**
   * Convert reject combined response options to SelectTextArea format.
   * 2+ options → dropdown; 0–1 → plain textarea (a single option pre-fills via
   * templateRejectComment). Scoped to the reject action (reject field text +
   * REJECT-tagged company comments), so approve / request-change responses
   * never appear in the reject modal.
   */
  get rejectTextAreaOptions() {
    const allOptions = this.combinedResponseOptions('reject');
    return allOptions.length > 1 ? allOptions : [];
  }

  /**
   * Convert request-change combined response options to SelectTextArea format.
   * 2+ options → dropdown; 0–1 → plain textarea (a single option pre-fills via
   * templateRequestChangeComment). Scoped to the request-change action (revise
   * field text + REVISE-tagged company comments), so approve / reject responses
   * never appear in the request-change modal.
   */
  get requestChangeTextAreaOptions() {
    const allOptions = this.combinedResponseOptions('requestChange');
    return allOptions.length > 1 ? allOptions : [];
  }

  /**
   * Whether the application uses Course Cash payment method
   */
  get isCourseCashPayment() {
    return this.args.application.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH';
  }

  /**
   * Whether the employee has completed their cardholder profile (accepted T&C + DOB)
   */
  get employeeHasCardholder() {
    return (this.args.application.tasProgramInstance?.employee?.pcsCardHolders?.length ?? 0) > 0;
  }

  /**
   * Determines if "Submit for User" action should be available in the menu
   * Available when:
   * 1. Application is in ATTEND or DEFINE_COURSES (DRAFT) state
   * 2. User has approval permissions
   *
   * Note: Missing requirements (courses, fields, attachments) are shown in the modal
   * rather than preventing the menu item from being clickable.
   */
  get canSubmitForUser() {
    const state = this.args.application.state;

    const allowedStates = ['TAS.ApplicationState.ATTEND', 'TAS.ApplicationState.DEFINE_COURSES'];

    return allowedStates.includes(state) && this.hasApprovalPermissions;
  }

  /**
   * Whether submit-for-user should be blocked for DRAFT course cash apps
   * without a cardholder profile. The employee must accept T&C and enter DOB first.
   */
  get isDraftCourseCashWithoutCardholder() {
    return (
      this.args.application.state === 'TAS.ApplicationState.DEFINE_COURSES' &&
      this.isCourseCashPayment &&
      !this.employeeHasCardholder
    );
  }

  get requiresGradePointAverageForSubmitForUser() {
    const application = this.args.application;
    const template = application.tasProgramInstance?.tasProgramTemplate;
    const isTypeTwoEvidence =
      application.typeClassification === 'TAS.ProgramType.2' ||
      application.typeClassification === 'TAS.ProgramType.4.2';
    const isTypeThreeEvidenceInAttend =
      (application.typeClassification === 'TAS.ProgramType.3' ||
        application.typeClassification === 'TAS.ProgramType.4.3') &&
      application.state === 'TAS.ApplicationState.ATTEND';

    return Boolean(
      template?.calculateTotalByGPA && (isTypeTwoEvidence || isTypeThreeEvidenceInAttend)
    );
  }

  get hasGradePointAverageForSubmitForUser() {
    const gradePointAverage = getSingleValueForTasField(
      'GRADE_POINT_AVERAGE',
      this.args.application.fields
    );
    return Number(gradePointAverage) > 0;
  }

  /**
   * Determines which adapter method to call when submitting for user
   * - ATTEND state: always requestCourseEvidence (evidence submission after attending)
   * - DEFINE_COURSES (DRAFT) state: depends on program type
   *   - Type 2 / 4.2 → requestCourseEvidence
   *   - Type 3 / 4.3 → requestCoursesApproval
   */
  get submitForUserAdapterMethod(): string {
    const state = this.args.application.state;

    // ATTEND state always submits evidence (existing behavior)
    if (state === 'TAS.ApplicationState.ATTEND') {
      return 'requestCourseEvidence';
    }

    // DEFINE_COURSES (DRAFT): route based on type classification
    const typeClassification = this.args.application.typeClassification;
    const actionMap: Record<string, string> = {
      'TAS.ProgramType.2': 'requestCourseEvidence',
      'TAS.ProgramType.3': 'requestCoursesApproval',
      'TAS.ProgramType.4.2': 'requestCourseEvidence',
      'TAS.ProgramType.4.3': 'requestCoursesApproval',
    };

    return actionMap[typeClassification] || 'requestCourseEvidence';
  }

  /**
   * Check if user has permissions to submit on behalf of employee
   */
  get hasApprovalPermissions(): boolean {
    const user = this.sessionContext.user;

    return Boolean(
      user?.isEmployeeAdmin ||
      user?.isAccountOwner ||
      user?.isTioAdmin ||
      user?.isTasTioApprover ||
      (this.args.application.isPrepaidProgram && user?.isPayOps)
    );
  }

  /**
   * Whether the policy override option should be available
   * Only admins can override policies, and only if not already overridden
   */
  get canOverridePolicies() {
    return this.features.isAdminApp && !this.args.application.ignorePolicies;
  }

  /**
   * Whether the In Collections toggle should be shown in the menu
   * Only available if the company has enabled the isInCollectionsEnabled setting
   */
  get showInCollections() {
    const companySetting =
      this.args.application.tasProgramInstance?.employee?.company?.companySetting;
    return companySetting?.tuitionAssistance?.isInCollectionsEnabled === true;
  }

  /**
   * Whether the application is currently marked as In Collections
   */
  get isInCollections() {
    return this.args.application.isInCollections;
  }

  /**
   * Determines if application is in a rescindable state
   * Rescindable states are application states that can be reverted
   */
  get inRescindableState() {
    const alwaysRescindableStates = new Set([
      'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL',
      'TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL',
      'TAS.ProgramInstanceState.PENDING_WITHDRAWAL_APPROVAL',
      'TAS.ProgramInstanceState.PRIMED',
      'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
    ]);

    const state = this.args.application.state;
    const isPaid = this.args.application.isPaid;

    // Always rescindable states
    if (alwaysRescindableStates.has(state)) {
      return true;
    }

    // Special handling for ATTEND and FULFILLED
    if (state === 'TAS.ApplicationState.ATTEND' || state === 'TAS.ApplicationState.FULFILLED') {
      return !isPaid;
    }

    return false;
  }

  /**
   * Determines if user has permission to rescind the application
   * User can rescind if they have the same role as the previous approver
   */
  get userCanRescindApp() {
    const user = this.sessionContext.user;

    // TIO-ADMIN context: Superusers can rescind anything
    if (user?.isTioAdmin) {
      const userRoles: readonly RoleView[] = user?.uniqueRolesByRelationshipType || [];
      if (userRoles.some((role) => role.relationshipType === 'TAS.Admin.SUPERUSER')) {
        return true;
      }
      // Fall through to role matching for non-superuser admins
    }

    // EMPLOYEE context: Account owner or employee admin can always rescind
    if (user?.isAccountOwnerOrEmployeeAdmin) {
      return true;
    }

    // Find the approver role for role-based permission check
    const transitionLog = this.args.application.transitionLog || [];
    const currentState = this.args.application.state;

    // Determine which approval stage we're in to avoid matching approvers from earlier stages
    const isEvidenceStage =
      currentState.includes('EVIDENCE') || currentState === 'TAS.ApplicationState.FULFILLED';
    const isCoursesStage =
      currentState.includes('COURSES') || currentState === 'TAS.ApplicationState.ATTEND';

    const relevantEvents = isEvidenceStage
      ? ['request_evidence_approval', 'approve_evidence', 'decline_evidence', 'reject_evidence']
      : isCoursesStage
        ? ['request_courses_approval', 'approve_courses', 'decline_courses', 'reject_courses']
        : [];

    // Find the most recent transition with approver info for the current stage
    // Backend auto-approvals create transitions with empty reason: {} objects
    let transitionWithApprover;
    for (let i = transitionLog.length - 1; i >= 0; i--) {
      const transition = transitionLog[i];
      if (
        transition?.reason?.by?.actingWithRole?.relationshipType &&
        (relevantEvents.length === 0 || relevantEvents.includes(transition.event))
      ) {
        transitionWithApprover = transition;
        break;
      }
    }

    if (!transitionWithApprover) {
      return false; // No approver found, can't determine permission
    }

    // Safe to use non-null assertions here because loop condition guarantees these exist
    const approverRoleType = transitionWithApprover.reason!.by!.actingWithRole.relationshipType;

    // TIO-ADMIN: Check if user has matching role
    if (user?.isTioAdmin) {
      const userRoles: readonly RoleView[] = user?.uniqueRolesByRelationshipType || [];
      return userRoles.some((role) => role.relationshipType === approverRoleType);
    }

    // EMPLOYEE: the approving role must be one held here. Matching a single
    // "current" role would hide rescind from the very approver who just approved,
    // since the actor builder picks their TAS role while a session exposes several.
    const heldHere = this.sessionContext.rolesForCurrentCompany ?? [];
    return heldHere.some((role) => role.relationshipType === approverRoleType);
  }

  /**
   * Determines if rescind button should be shown
   * Combined visibility check for all conditions
   */
  get showRescindButton() {
    return this.inRescindableState && this.userCanRescindApp;
  }

  /**
   * Determines if application is in a rejected state
   * Used to show "Reopen Application" action instead of "Reject Application"
   */
  get isRejected(): boolean {
    const rejectedStates = [
      'TAS.ProgramInstanceState.PROGRAM_REJECTED',
      'TAS.ProgramInstanceState.COMPLETION_REJECTED',
      'TAS.ProgramInstanceState.WITHDRAWAL_REJECTED',
      'TAS.ApplicationState.COURSES_REJECTED',
      'TAS.ApplicationState.EVIDENCE_REJECTED',
      'TAS.ApplicationState.REJECTED_PER_POLICY',
    ];

    return rejectedStates.includes(this.args.application.state);
  }

  /**
   * Determines which adapter to use for reopening application
   * Returns 'tasProgramInstance' or 'tasApplication' based on state
   */
  get setAdapter(): string | undefined {
    const splitState = this.args.application.state.split('.').slice(0, 2).join('.');
    return splitState === 'TAS.ProgramInstanceState'
      ? 'tasProgramInstance'
      : splitState === 'TAS.ApplicationState'
        ? 'tasApplication'
        : undefined;
  }

  /**
   * Determines which adapter method to call for reopening
   * Maps rejected states to their corresponding adapter methods
   */
  get adapterMethod(): string {
    const actionMap: Record<string, string> = {
      'TAS.ProgramInstanceState.PROGRAM_REJECTED': 'requestChanges',
      'TAS.ProgramInstanceState.COMPLETION_REJECTED': 'requestProgramCompletionChanges',
      'TAS.ProgramInstanceState.WITHDRAWAL_REJECTED': 'requestProgramWithdrawalChanges',
      'TAS.ApplicationState.COURSES_REJECTED': 'requestChanges',
      'TAS.ApplicationState.EVIDENCE_REJECTED': 'requestCourseEvidenceChanges',
      'TAS.ApplicationState.REJECTED_PER_POLICY': 'rescindLastApproverAction',
    };
    return actionMap[this.args.application.state] || '';
  }

  /**
   * Check if last transition was auto-rejection from ATTEND state
   * Used to determine if approved total should be reset during reopen
   */
  get lastTransitionLogAutoRejectedFromAttend(): boolean {
    const log = this.args.application.transitionLog;
    const lastTransition = log?.at(-1);
    return (
      log?.length >= 2 &&
      lastTransition?.to === 'TAS.ApplicationState.EVIDENCE_REJECTED' &&
      lastTransition.from === 'TAS.ApplicationState.ATTEND'
    );
  }

  /**
   * Get the approved total before the last rejection
   * Used to reset approved total if application was auto-rejected
   */
  get lastApprovedTotalBeforeRejection(): number | undefined {
    return this.args.application.approvedTotalLog?.at(-1)?.from;
  }

  /**
   * Validate requirements for submitting on user's behalf
   * Returns array of missing requirement descriptions
   *
   * ATTEND state: validates evidence requirements (grades, evidence attachments, GPA)
   * DEFINE_COURSES (DRAFT) state: validates based on program type
   *   - Type 2 / 4.2: evidence attachments (or pre-approval attachments)
   *   - Type 3 / 4.3: pre-approval attachments
   */
  get missingRequirementsForSubmitForUser(): string[] {
    const application = this.args.application;
    const state = application.state;

    // DRAFT state: validate based on program type
    if (state === 'TAS.ApplicationState.DEFINE_COURSES') {
      return this.missingRequirementsForDraftSubmit;
    }

    // ATTEND state: existing evidence validation
    const { tasProgramInstance, isMissingCourseGrades, isMissingRequiredEvidence } = application;

    let missingRecs: string[] = [];
    const courseGradeRequired = tasProgramInstance.tasProgramTemplate.courseGradeRequired;

    if (courseGradeRequired && isMissingCourseGrades) {
      missingRecs = [...missingRecs, ' course grade(s)'];
    }

    if (isMissingRequiredEvidence) {
      missingRecs = [...missingRecs, ' evidence attachment'];
    }

    if (
      this.requiresGradePointAverageForSubmitForUser &&
      !this.hasGradePointAverageForSubmitForUser
    ) {
      missingRecs = [...missingRecs, ' GPA'];
    }

    return missingRecs;
  }

  /**
   * Validate requirements for DRAFT (DEFINE_COURSES) state submission
   * Dynamically checks all required template fields, custom fields, attachments, and courses
   */
  @cached
  get missingRequirementsForDraftSubmit(): string[] {
    const application = this.args.application;
    const typeClassification = application.typeClassification;
    const template = application.tasProgramInstance?.tasProgramTemplate;
    let missingRecs: string[] = [];

    // Must have at least one active course
    if (!application.activeCourses?.length) {
      missingRecs = [...missingRecs, ' at least one course'];
    }

    if (template?.fields) {
      const templateFields = template.fields as Record<
        string,
        {
          province?: string;
          required?: boolean;
          visible?: boolean;
          label?: string;
          values?: unknown[];
        }
      >;

      for (const [fieldName, config] of Object.entries(templateFields)) {
        if (!config?.required || !config?.visible || SYSTEM_FIELDS.has(fieldName)) {
          continue;
        }

        const province = config.province;

        // APPLICATION province: check application.fields
        if (province === 'APPLICATION') {
          const value = getSingleValueForTasField(
            fieldName as TemplateFieldName,
            application.fields
          );
          if (value === null || value === undefined || value === '') {
            missingRecs = [...missingRecs, ` ${config.label || fieldName}`];
          }
        }

        // APPLICANT or PROGRAM province: check instance.fields
        // PROGRAM province includes employee-fillable fields like Institution Name,
        // Program Specialization, etc. that are stored on the program instance
        if (province === 'APPLICANT' || province === 'PROGRAM') {
          const instanceFields = application.tasProgramInstance?.fields;
          const value = getSingleValueForTasField(fieldName as TemplateFieldName, instanceFields);
          if (value === null || value === undefined || value === '') {
            missingRecs = [...missingRecs, ` ${config.label || fieldName}`];
          }
        }

        // COURSE province: checked per-course below (skip here)
      }
    }

    // Check required course-province template fields on each active course
    if (template?.fields && application.activeCourses?.length) {
      const templateFields = template.fields as Record<
        string,
        {
          province?: string;
          required?: boolean;
          visible?: boolean;
          label?: string;
          values?: unknown[];
        }
      >;

      const requiredCourseFields = Object.entries(templateFields).filter(
        ([fieldName, config]) =>
          config?.province === 'COURSE' &&
          config?.required &&
          config?.visible &&
          !SYSTEM_FIELDS.has(fieldName)
      );

      for (const course of application.activeCourses) {
        const courseName =
          getSingleValueForTasField('COURSE_NAME', course.fields) || 'Unnamed course';

        for (const [fieldName, config] of requiredCourseFields) {
          const value = getSingleValueForTasField(fieldName as TemplateFieldName, course.fields);
          if (value === null || value === undefined || value === '') {
            missingRecs = [...missingRecs, ` ${config.label || fieldName} (${courseName})`];
          }
        }

        const courseCustomFields = course.customFields || [];
        const missingCourseCustom = courseCustomFields.filter(
          (f) =>
            f.required &&
            f.visible &&
            (!f.values?.length ||
              f.values[0] === undefined ||
              f.values[0] === null ||
              f.values[0] === '')
        );
        if (missingCourseCustom.length) {
          missingRecs = [
            ...missingRecs,
            ...missingCourseCustom.map((f) => ` ${f.label} (${courseName})`),
          ];
        }
      }
    }

    const appCustomFields = application.customFields || [];
    const missingCustomFields = appCustomFields.filter(
      (f) =>
        f.required &&
        f.visible &&
        (!f.values?.length ||
          f.values[0] === undefined ||
          f.values[0] === null ||
          f.values[0] === '')
    );
    if (missingCustomFields.length) {
      missingRecs = [...missingRecs, ...missingCustomFields.map((f) => ` ${f.label}`)];
    }

    const instanceCustomFields = application.tasProgramInstance?.customFields || [];
    const missingInstanceCustomFields = instanceCustomFields.filter(
      (f) =>
        f.required &&
        f.visible &&
        (!f.values?.length ||
          f.values[0] === undefined ||
          f.values[0] === null ||
          f.values[0] === '')
    );
    if (missingInstanceCustomFields.length) {
      missingRecs = [...missingRecs, ...missingInstanceCustomFields.map((f) => ` ${f.label}`)];
    }

    // Attachment requirements by type
    switch (typeClassification) {
      case 'TAS.ProgramType.2':
      case 'TAS.ProgramType.4.2': {
        // Type 2/4.2: needs evidence attachments OR pre-approval attachments
        const hasAttachments =
          application.hasRequiredEvidenceAttachments ||
          application.hasRequiredPreApprovalAttachments;
        if (!hasAttachments) {
          missingRecs = [...missingRecs, ' required attachment(s)'];
        }
        if (
          this.requiresGradePointAverageForSubmitForUser &&
          !this.hasGradePointAverageForSubmitForUser
        ) {
          missingRecs = [...missingRecs, ' GPA'];
        }
        break;
      }
      case 'TAS.ProgramType.3':
      case 'TAS.ProgramType.4.3':
        // Type 3/4.3: needs pre-approval attachments
        if (!application.hasRequiredPreApprovalAttachments) {
          missingRecs = [...missingRecs, ' required attachment(s)'];
        }
        break;
      default:
        break;
    }

    return missingRecs;
  }

  /**
   * Format missing requirements as user-friendly alert message
   */
  get missingRecsAlertForSubmitForUser(): string {
    return `You are missing the following requirement(s):${this.missingRequirementsForSubmitForUser}`;
  }

  /**
   * Determine which menu items should be disabled
   */
  get disabledMenuKeys(): string[] {
    const disabled: string[] = [];
    if (!this.canRejectApplication) {
      disabled.push('reject');
    }
    if (!this.isRejected) {
      disabled.push('reopen');
    }
    if (!this.canRequestChange) {
      disabled.push('requestChange');
    }
    if (!this.canSubmitForUser) {
      disabled.push('submitForUser');
    }
    if (!this.showRescindButton) {
      disabled.push('rescind');
    }
    if (!this.canReassignApprover) {
      disabled.push('reassign');
    }
    if (!this.canOverridePolicies) {
      disabled.push('policyOverride');
    }
    return disabled;
  }

  /**
   * Selects the correct adapter method based on application state
   */
  get rejectionCall() {
    return this.args.application.state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL'
      ? 'rejectCourseEvidence'
      : 'rejectCourses';
  }

  /**
   * Handle icon button click - opens the corresponding drawer
   */
  handleDrawerClick = (drawerType: string) => {
    this.args.onDrawerOpen(drawerType);
  };

  /**
   * Handle CourseCash icon click - delegates to external callback
   */
  handleCourseCashClick = () => {
    this.args.onCourseCashOpen?.();
  };

  get courseCashTooltip() {
    // Compose the localized "… Transactions" label with the canonical brand mark
    // injected as a SafeString, so ember-intl leaves the mark's HTML unescaped.
    return this.intl.t('common.tas.course_cash.transactions_tooltip', {
      htmlSafe: true,
      courseCash: this.intl.t('common.tas.course_cash.default', { htmlSafe: true }),
    });
  }

  /**
   * Check if a specific drawer is currently active
   */
  isDrawerActive = (drawerType: string): boolean => {
    return this.args.activeDrawer === drawerType;
  };

  /**
   * Quick-select amounts for reject-time repayment, mirroring the Request Repayment
   * modal so the two surfaces offer the same choices. Only options with a non-zero
   * amount survive, so a reimbursement-only application shows one and a mixed-funding
   * one shows all three.
   */
  @cached
  get repaymentAmountOptions() {
    const amounts = this.args.application.amounts;

    const prefix = 'tuition_assistance.application_details.reject_amount_option';

    const options = [
      {
        key: 'full',
        label: this.intl.t(`${prefix}.full`),
        description: this.intl.t(`${prefix}.full_description`),
        amount: Math.abs(amounts?.refund_limit ?? 0),
      },
      {
        key: 'reimbursement',
        label: this.intl.t(`${prefix}.reimbursement`),
        description: this.intl.t(`${prefix}.reimbursement_description`),
        amount: Math.abs(amounts?.paid ?? 0),
      },
      {
        key: 'courseCash',
        // The brand mark is HTML-bearing, so it is interpolated from the canonical key rather
        // than retyped — SelectableCard renders @label as text, so the plain `label` variant is
        // used here and the styled mark stays with the surfaces that can render it.
        label: this.intl.t(`${prefix}.course_cash`, {
          courseCash: this.intl.t('common.tas.course_cash.label'),
        }),
        description: this.intl.t(`${prefix}.course_cash_description`),
        amount: Math.abs(amounts?.course_cash?.used ?? 0),
      },
    ].filter((option) => option.amount > 0);

    // `full` is the sum of the other two, so a single funded component makes it a duplicate card.
    const components = options.filter((option) => option.key !== 'full');
    const full = options.find((option) => option.key === 'full');

    return full && components.some((option) => option.amount === full.amount)
      ? components
      : options;
  }

  /** A lone card still names which funds the amount is — the input below is the adjustment. */
  get showRepaymentAmountOptions() {
    return this.repaymentAmountOptions.length > 0;
  }

  /** Ceiling for a hand-typed amount — what is actually available to claw back. */
  get maxRepaymentAmount() {
    return Math.abs(this.args.application.amounts?.refund_limit ?? 0);
  }

  get isRejectFormValid() {
    if (!this.rejectNote) {
      return false;
    }
    // An opted-in repayment must carry a real amount; request_repayment rejects
    // zero outright, and a silent 0 would leave the application rejected with
    // nothing recovered.
    return !this.requestRepaymentOnReject || this.requestedRepaymentAmount > 0;
  }

  #seedRepaymentAmount(amountCents: number) {
    this.requestedRepaymentAmount = amountCents;
    this.requestedRepaymentAmountDisplay = amountCents ? String(amountCents / 100) : '';
  }

  /**
   * Whether the hold is offerable at all — mirrors the backend's `may_hold?`, which only
   * transitions from ACTIVE. A participant already held (or inactive) cannot be held again;
   * offering the checkbox anyway makes the hold call fail after the rejection has committed,
   * stranding the approver on "rejected, but follow-up failed" with a retry that can never
   * succeed. An existing hold also must not be re-noted: reopening releases only the hold
   * THIS rejection placed, so an unrelated hold keeps its own provenance.
   */
  get canHoldOnReject() {
    return Boolean(this.args.application.tasProgramInstance?.employee?.tasParticipant?.isActive);
  }

  openRejectModal = () => {
    if (!this.rejectNote) {
      this.rejectNote = this.templateRejectComment;
    }
    this.rejectError = null;
    // Fresh reject attempt — clear any phase state left from a prior partial failure.
    this.#rejectionCommitted = false;
    this.#repaymentRequested = false;

    // Default both consequences on when there is money to recover, and seed the
    // amount from live `amounts` at open time rather than at construction.
    const eligible = this.args.application.canRepayOnReject;
    this.requestRepaymentOnReject = eligible;
    this.holdEmployeeOnReject = eligible && this.canHoldOnReject;
    const seeded = eligible ? Math.abs(this.args.application.amounts?.refund_limit ?? 0) : 0;
    // `full` is dropped when it duplicates a component, so select whichever card holds the seed.
    this.repaymentAmountSelection =
      this.repaymentAmountOptions.find((option) => option.amount === seeded)?.key ?? 'full';
    this.#seedRepaymentAmount(seeded);

    this.isRejectModalOpen = true;
  };

  closeRejectModal = () => {
    this.isRejectModalOpen = false;
    this.rejectError = null;
  };

  toggleRequestRepaymentOnReject = () => {
    this.requestRepaymentOnReject = !this.requestRepaymentOnReject;
  };

  toggleHoldEmployeeOnReject = () => {
    this.holdEmployeeOnReject = !this.holdEmployeeOnReject;
  };

  selectRepaymentAmount = (key: string) => {
    this.repaymentAmountSelection = key;

    const option = this.repaymentAmountOptions.find((opt) => opt.key === key);
    if (option) {
      this.#seedRepaymentAmount(option.amount);
    }
  };

  updateRequestedRepaymentAmount = (_maskedValue: unknown, event?: InputEvent) => {
    const dollars = Number(getInputmaskUnmaskedValue(event));
    const cents = Number.isFinite(dollars) ? Math.round(dollars * 100) : 0;

    // Cap at the amount actually available to claw back, matching the Request Repayment modal.
    // request_repayment raises above its limit, and by then the rejection has already committed
    // — the approver would be left with "rejected, but follow-up failed" and nothing recovered.
    this.requestedRepaymentAmount = Math.min(cents, this.maxRepaymentAmount);

    // Deliberately NOT updating requestedRepaymentAmountDisplay here: rewriting @value on every
    // keystroke remounts the input and steals focus mid-type (same reason the Request Repayment
    // modal leaves it alone).
    this.repaymentAmountSelection = 'custom';
  };

  updateRejectNote = (value: string) => {
    this.rejectNote = value;
  };

  openSubmitForUserModal = () => {
    this.submitForUserModalOpen = true;
    this.submitForUserError = null;
  };

  closeSubmitForUserModal = () => {
    this.submitForUserModalOpen = false;
    this.submitForUserError = null;
  };

  openRescindModal = () => {
    this.isRescindModalOpen = true;
    this.rescindError = null;
  };

  closeRescindModal = () => {
    this.isRescindModalOpen = false;
    this.rescindError = null;
  };

  openApproveModal = () => {
    this.isApproveModalOpen = true;
  };

  closeApproveModal = () => {
    this.isApproveModalOpen = false;
  };

  updateApproveCommentDraft = (value: string) => {
    this.approveCommentDraft = value;
  };

  openRequestChangeModal = () => {
    this.isRequestChangeModalOpen = true;
    if (!this.requestChangeNote) {
      this.requestChangeNote = this.templateRequestChangeComment;
    }
    this.requestChangeError = null;
  };

  closeRequestChangeModal = () => {
    this.isRequestChangeModalOpen = false;
    this.requestChangeError = null;
  };

  updateRequestChangeNote = (value: string) => {
    this.requestChangeNote = value;
  };

  openReassignDrawer = () => {
    this.args.onDrawerOpen('reassign-approver');
  };

  handleReassignClick = (approver: ApplicationApprovalSummaryApprover) => {
    this.selectedApproverToReassign = approver;
    this.isReassignModalOpen = true;
  };

  closeReassignModal = () => {
    this.isReassignModalOpen = false;
    this.selectedApproverToReassign = null;
  };

  openReopenModal = () => {
    this.isReopenModalOpen = true;
    this.reopenError = null;
  };

  closeReopenModal = () => {
    this.isReopenModalOpen = false;
    this.reopenError = null;
  };

  openOverrideModal = () => {
    this.isOverrideModalOpen = true;
  };

  closeOverrideModal = () => {
    this.isOverrideModalOpen = false;
  };

  openInCollectionsModal = () => {
    this.isInCollectionsModalOpen = true;
    this.inCollectionsError = null;
  };

  closeInCollectionsModal = () => {
    this.isInCollectionsModalOpen = false;
    this.inCollectionsError = null;
  };

  toggleInCollectionsTask = dropTask(async () => {
    this.inCollectionsError = null;

    try {
      await toggleTasApplicationInCollections(this.store, this.args.application);
      await reloadRecord(this.store, this.args.application);
      this.closeInCollectionsModal();
    } catch (e) {
      console.error('Error toggling In Collections:', e);
      this.inCollectionsError = e instanceof Error ? e.message : String(e);
    }
  });

  confirmPolicyOverrideTask = dropTask(async () => {
    const today = this.intl.formatDate(new Date(), {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    });

    const changelogEntry = `Ignore Policies set by ${this.sessionContext.user.person.fullName} on ${today}`;
    this.args.application.ignorePolicies = true;

    try {
      // WarpDrive mutation builder (Phase 2.7) — replaces
      // application.save({ adapterOptions: { changelog } }); the changelog rides as
      // data.attributes._changelog on the PATCH (ApplicationSerializer parity).
      await saveExistingRecordWithChangelog(this.store, this.args.application, changelogEntry);
      this.isOverrideModalOpen = false;
    } catch {
      this.args.application.ignorePolicies = false;
    }
  });

  openMoreActionsPopover = () => {
    this.isMoreActionsPopoverOpen = true;
  };

  closeMoreActionsPopover = () => {
    this.isMoreActionsPopoverOpen = false;
  };

  handleMoreActionsMenu = (key: string) => {
    this.closeMoreActionsPopover();

    // Modals use @renderInPlace={{true}} to avoid portal nesting issues with the popover
    // See: https://github.com/josemarluedke/frontile/issues/431
    switch (key) {
      case 'reject':
        this.openRejectModal();
        break;
      case 'reopen':
        this.openReopenModal();
        break;
      case 'requestChange':
        this.openRequestChangeModal();
        break;
      case 'submitForUser':
        this.openSubmitForUserModal();
        break;
      case 'rescind':
        this.openRescindModal();
        break;
      case 'reassign':
        this.openReassignDrawer();
        break;
      case 'policyOverride':
        this.openOverrideModal();
        break;
      case 'inCollections':
        this.openInCollectionsModal();
        break;
    }
  };

  /**
   * Mirrors the employee submit flow (prepareTotalsMiscFieldsAndSave + validateLimits)
   * for DRAFT (DEFINE_COURSES) state submissions on behalf of the user.
   */
  private async prepareDraftSubmission(application: TasApplicationModel) {
    const template = application.tasProgramInstance?.tasProgramTemplate;
    if (!template) {
      throw new Error(
        'Cannot prepare draft submission because tasProgramTemplate is missing for the application.'
      );
    }

    // 1. Set SEMESTER_CODE from COURSES_BEGIN_DATE
    if (template.semesterCodeIsVisible) {
      const beginDate = getSingleValueForTasField('COURSES_BEGIN_DATE', application.fields);
      if (beginDate) {
        setTrackedTasFieldValue(
          application,
          'SEMESTER_CODE',
          setSemesterCodeString(String(beginDate))
        );
      }
    }

    // 2. Set payment method for direct pay programs
    const directPayPaymentMethod = template.isDirectPayProgram
      ? application.paymentMethod ||
        getSingleValueForTasField('PAYMENT_TYPE_FOR_DIRECT_PAY', application.fields) ||
        'TAS.AssistanceManner.COURSE_CASH'
      : application.paymentMethod;

    if (template.isDirectPayProgram) {
      setTrackedTasFieldValue(application, 'PAYMENT_TYPE_FOR_DIRECT_PAY', directPayPaymentMethod);
    }

    // 3. Set financial totals (mirrors setSubtotals / approved totals logic)
    const typeClassification = application.typeClassification;
    const isCourseCash =
      template.isDirectPayProgram && directPayPaymentMethod === 'TAS.AssistanceManner.COURSE_CASH';

    // Determine which total fields to use based on type
    const usesFinal =
      typeClassification === 'TAS.ProgramType.2' || typeClassification === 'TAS.ProgramType.4.2';

    if (isCourseCash) {
      // Course Cash: set card and non-card subtotals
      const courseCashField = usesFinal ? 'CARD_FINAL_APPROVED_TOTAL' : 'CARD_PRE_APPROVED_TOTAL';
      const nonCourseCashField = usesFinal ? 'FINAL_APPROVED_TOTAL' : 'PRE_APPROVED_TOTAL';

      setTrackedTasFieldValue(
        application,
        courseCashField,
        application.requestedCourseCashSubtotalLessScholarships
      );
      setTrackedTasFieldValue(
        application,
        nonCourseCashField,
        application.requestedNonCourseCashSubtotal
      );
    } else {
      // Non-Course Cash: clear card totals, set approved total
      setTrackedTasFieldValue(application, 'CARD_PRE_APPROVED_TOTAL', null);
      setTrackedTasFieldValue(application, 'CARD_FINAL_APPROVED_TOTAL', null);

      const approvedField = usesFinal ? 'FINAL_APPROVED_TOTAL' : 'PRE_APPROVED_TOTAL';
      let requestedAmount = application.requestedTotal;

      // Claims financials: total is always 0 at submission
      if (template.calculateTotalByClaimsFinancials) {
        requestedAmount = 0;
      } else if (application.requiresGPA) {
        // GPA-based: cap at GPA tier amount (only Type 2 / 4.2)
        const gpa = Number(
          getSingleValueForTasField('GRADE_POINT_AVERAGE', application.fields) || 0
        );
        const gpaAmount = this.tuitionAssistance.setTotalBasedOnGPA(gpa);
        requestedAmount = Math.min(gpaAmount, application.requestedTotal);
      }

      setTrackedTasFieldValue(application, approvedField, requestedAmount);
    }

    // 4. Save the application with computed fields
    await saveRecord(this.store, application);
  }

  submitForUserTask = dropTask(async () => {
    this.submitForUserError = null;

    try {
      const application = this.args.application;
      const isDraft = application.state === 'TAS.ApplicationState.DEFINE_COURSES';

      // For DRAFT state, mirror the employee submit flow
      if (isDraft) {
        await this.prepareDraftSubmission(application);

        // Validate limits before submitting
        const employee = application.tasProgramInstance?.employee;
        if (employee) {
          const limitsResult =
            await this.tuitionAssistance.validateAmountRequestedAgainstCurrentLimitsStatus(
              application,
              employee
            );
          if (!limitsResult.can_submit) {
            const reductionAmount = application.isCreditBased
              ? `${limitsResult.reduction_needed} credits`
              : this.intl.formatNumber(limitsResult.reduction_needed / 100, {
                  style: 'currency',
                  currency: 'USD',
                });
            this.submitForUserError = `The requested amount exceeds the employee's remaining benefit. Please reduce by ${reductionAmount}.`;
            return;
          }
        }
      }

      // Call the appropriate transition based on state and program type.
      // WarpDrive mutation builder (Phase 2.7) — replaces
      // adapter[this.submitForUserAdapterMethod](application) (triggerActionAndReload →
      // request-courses-approval / request-evidence-approval transition + reload).
      const event = TAS_TRANSITION_EVENT_FOR_METHOD[this.submitForUserAdapterMethod];
      if (!event) {
        throw new Error(
          `No TAS transition event for submitForUserAdapterMethod="${this.submitForUserAdapterMethod}"`
        );
      }
      await this.store.request(
        transitionTasApplicationRequest(
          application.id as string,
          event,
          buildTasActorFromSession(this.sessionContext),
          '',
          buildTasTransitionFieldData(application.fields)
        )
      );

      // Reload application to reflect state change
      await reloadRecord(this.store, application);

      this.closeSubmitForUserModal();
    } catch (e) {
      console.error(e);
      // A 422's message is the transport string ("[422 …] PATCH …"); prefer the JSON:API detail.
      this.submitForUserError = parseApiError(e, e instanceof Error ? e.message : String(e));
    }
  });

  rescindStateAction = dropTask(async () => {
    this.rescindError = null;

    try {
      // WarpDrive mutation builder (Phase 2.7) — replaces
      // adapter.rescindLastApproverAction(application) (PATCH /transition/state/rescind,
      // NO body, NO actor). The reload below mirrors the legacy follow-up.
      await this.store.request(
        rescindLastApproverActionRequest(this.args.application.id as string)
      );

      // Reload application to reflect backend changes
      await reloadRecord(this.store, this.args.application);

      this.closeRescindModal();
    } catch (e) {
      console.error(e);
      this.rescindError = e instanceof Error ? e.message : String(e);
    }
  });

  /**
   * Submit Request Change action
   * Calls adapter method with application and comment
   * Closes modal and refreshes page on success
   */
  confirmRequestChangeTask = dropTask(async () => {
    this.requestChangeError = null;

    try {
      // WarpDrive mutation builder (Phase 2.7) — replaces
      // adapter[this.requestChangeAdapterMethod](application, requestChangeNote)
      // (triggerActionAndReload → decline-courses / decline-evidence transition + reload).
      const event = TAS_TRANSITION_EVENT_FOR_METHOD[this.requestChangeAdapterMethod];
      if (!event) {
        throw new Error(
          `No TAS transition event for requestChangeAdapterMethod="${this.requestChangeAdapterMethod}"`
        );
      }
      await this.store.request(
        transitionTasApplicationRequest(
          this.args.application.id as string,
          event,
          buildTasActorFromSession(this.sessionContext),
          this.requestChangeNote,
          buildTasTransitionFieldData(this.args.application.fields)
        )
      );

      // Success - close modal and refresh
      this.requestChangeNote = '';
      this.closeRequestChangeModal();

      // Reload the application to get updated state
      await reloadRecord(this.store, this.args.application, {
        include: 'tas-assistances,tas-program-instance.employee.tas-participant',
      });
    } catch (e) {
      console.error('Error submitting Request Change:', e);
      this.requestChangeError = e instanceof Error ? e.message : String(e);
    }
  });

  // RMP-603 item #1: rejecting a repayment-eligible application performs up to three
  // non-atomic backend steps — reject transition, request-repayment, participant hold.
  // These flags let a retry (the modal stays open on a follow-up failure) resume from
  // the first incomplete step instead of re-running the already-committed reject
  // transition or double-requesting repayment. Reset each time the modal is opened.
  #rejectionCommitted = false;
  #repaymentRequested = false;

  confirmRejectionTask = dropTask(async () => {
    this.rejectError = null;

    // Step 1 — reject transition (skipped if a prior attempt already committed it).
    if (!this.#rejectionCommitted) {
      try {
        const event = TAS_TRANSITION_EVENT_FOR_METHOD[this.rejectionCall];
        if (!event) {
          throw new Error(`No TAS transition event for rejectionCall="${this.rejectionCall}"`);
        }
        await this.store.request(
          transitionTasApplicationRequest(
            this.args.application.id as string,
            event,
            buildTasActorFromSession(this.sessionContext),
            this.rejectNote,
            buildTasTransitionFieldData(this.args.application.fields)
          )
        );
        // The reject deactivates the CourseCa$h card server-side, so the card has to come
        // back with the application or the Financial Details chip keeps reading Active.
        await reloadRecord(this.store, this.args.application, {
          include: 'pcs-cards.pcs-authorizations',
        });
        this.#rejectionCommitted = true;
      } catch (e) {
        // The reject transition itself failed — nothing has changed; surface it plainly.
        console.error(e);
        this.rejectError = e instanceof Error ? e.message : String(e);
        return;
      }
    }

    // Steps 2 & 3 — each is the approver's decision, taken from the modal. The reject
    // has already committed, so on failure we keep the modal open with an explicit
    // "rejected, but follow-up failed" message and leave the flags set: a retry reruns
    // only the unfinished step.
    try {
      if (this.args.application.canRepayOnReject) {
        const participantId = this.args.application.tasProgramInstance.employee.tasParticipant.id;

        if (this.requestRepaymentOnReject && !this.#repaymentRequested) {
          await this.store.request(
            requestRepaymentRequest(
              this.args.application.id as string,
              this.requestedRepaymentAmount,
              this.rejectNote
            )
          );
          await reloadRecord(this.store, this.args.application, {
            include: 'tas-assistances,tas-program-instance.employee.tas-participant',
          });
          // Set only on success. Setting it unconditionally would also mark a DECLINED
          // repayment as done, so an approver who ticks the box and retries after a failed
          // hold would silently skip the repayment they just asked for.
          this.#repaymentRequested = true;
        }

        if (this.holdEmployeeOnReject && this.canHoldOnReject) {
          await this.store.request(
            holdTasParticipantRequest(participantId as string, {
              note: rejectHoldNote(this.args.application.id as string, this.rejectNote),
            })
          );
        }
      }

      // Full success — reset for next time and close.
      this.#rejectionCommitted = false;
      this.#repaymentRequested = false;
      this.rejectNote = '';
      this.closeRejectModal();
      await loadTasApplicationHistory(this.store, this.args.application);
    } catch (e) {
      console.error(e);
      this.rejectError = this.intl.t(
        'tuition_assistance.application_details.reject_committed_followup_failed'
      );
    }
  });

  /**
   * Reopen a rejected application
   * Calls the appropriate adapter method based on rejection state
   * Resets approved total if application was auto-rejected from ATTEND
   */
  /**
   * `setAdapter` selects the resource family by state prefix and `adapterMethod`
   * the transition; each (family, method) pair routes to its matching builder.
   * Transition methods went through `triggerActionAndReload` (PATCH + plain
   * `reload()`), re-issued here; `rescindLastApproverAction` sent no body and did
   * not reload (the caller's trailing reload covers it).
   */
  private async dispatchReopenTransition(): Promise<void> {
    const application = this.args.application;
    const id = application.id as string;
    const actor = buildTasActorFromSession(this.sessionContext);

    if (this.setAdapter === 'tasProgramInstance') {
      switch (this.adapterMethod) {
        case 'requestChanges':
          await this.store.request(declineProgramRequest(id, actor));
          break;
        case 'requestProgramCompletionChanges':
          await this.store.request(requestProgramCompletionChangesRequest(id, actor));
          break;
        case 'requestProgramWithdrawalChanges':
          await this.store.request(requestProgramWithdrawalChangesRequest(id, actor));
          break;
        case 'rescindLastApproverAction':
          await this.store.request(rescindTasProgramInstanceRequest(id));
          return;
        default:
          return;
      }
      await reloadRecord(this.store, application);
      return;
    }

    // tas-application family — transitions carry the four-field snapshot.
    const fieldData = buildTasTransitionFieldData(application.fields);
    switch (this.adapterMethod) {
      case 'requestChanges':
        await this.store.request(declineCoursesRequest(id, actor, '', fieldData));
        break;
      case 'requestCourseEvidenceChanges':
        await this.store.request(declineEvidenceRequest(id, actor, '', fieldData));
        break;
      case 'rescindLastApproverAction':
        await this.store.request(rescindLastApproverActionRequest(id));
        return;
      default:
        return;
    }
    await reloadRecord(this.store, application);
  }

  reopenApplication = dropTask(async () => {
    this.reopenError = null;

    try {
      // The same (family, method) branch mapping is replicated in dispatchReopenTransition.
      await this.dispatchReopenTransition();

      // If the application was auto-rejected from the attend state — only ever an
      // ApplicationState transition, so updateApprovedTotal (tas-application) is the
      // correct endpoint here.
      if (this.lastTransitionLogAutoRejectedFromAttend) {
        // reset the approved total to the amount before the auto-rejection
        await this.store.request(
          updateApprovedTotalRequest(
            this.args.application.id as string,
            this.lastApprovedTotalBeforeRejection,
            'Reset total to amount prior to auto-rejection'
          )
        );
        await reloadRecord(this.store, this.args.application, { include: 'tas-assistances' });
      }

      // Reload application to reflect state change
      await reloadRecord(this.store, this.args.application);

      this.closeReopenModal();
    } catch (e) {
      console.error(e);
      this.reopenError = e instanceof Error ? e.message : String(e);
    }
  });

  <template>
    <div
      class="sticky bottom-0 left-0 right-0 z-20 tio-bg-surface border-t tio-border-default shadow-lg rounded-b-lg"
      data-test-approval-footer
      ...attributes
    >
      <div class="mx-auto px-2 py-2">
        {{! Unified Layout: Responsive flex container }}
        <div class="tio-col md:flex-row justify-between items-stretch md:items-center">
          <div class="tio-col-tight md:flex-row items-stretch md:items-center">
            <Button
              @intent="primary"
              @size="sm"
              @isRunning={{@approveButtonLoading}}
              @isDisabled={{not this.canApproveApplication}}
              class="md:w-auto w-full"
              {{on "click" this.openApproveModal}}
              data-test-approve-button
            >
              <span class="hidden md:inline">Approve Application</span>
              <span class="md:hidden">Approve</span>
            </Button>

            <Button
              @appearance="outlined"
              @intent="primary"
              @size="sm"
              @isDisabled={{not this.canRequestChange}}
              class="md:w-auto w-full"
              {{on "click" this.openRequestChangeModal}}
              data-test-footer-request-change-button
            >
              Request Change
            </Button>
          </div>

          {{! Icon Buttons Row - responsive sizing and spacing }}
          <div class="tio-row-extra-tight justify-around md:justify-end md:gap-1">
            {{#if this.showAiAnalysis}}
              <TooltipButton
                @tooltip={{t "tuition_assistance.application_approval.ai_analysis"}}
                @tooltipPlacement="top"
                @appearance="minimal"
                @intent="default"
                @size="sm"
                class="!p-2 !rounded-md"
                @isActive={{this.isDrawerActive "ai-analysis"}}
                data-test-button="footer-ai-analysis-button"
                {{on "click" (fn this.handleDrawerClick "ai-analysis")}}
              >
                <PhSparkle @size="20" class="md:hidden" />
                <PhSparkle @size="24" class="hidden md:block" />
              </TooltipButton>
            {{/if}}

            <TooltipButton
              @tooltip={{t "tuition_assistance.application_approval.comments"}}
              @tooltipPlacement="top"
              @appearance="minimal"
              @intent="default"
              @size="sm"
              class="!p-2 !rounded-md"
              @isActive={{this.isDrawerActive "comments"}}
              data-test-button="footer-comments-button"
              {{on "click" (fn this.handleDrawerClick "comments")}}
            >
              <PhChatCenteredText @size="20" class="md:hidden" />
              <PhChatCenteredText @size="24" class="hidden md:block" />
            </TooltipButton>

            <TooltipButton
              @tooltip={{if @expenseType @expenseType "Courses"}}
              @tooltipPlacement="top"
              @appearance="minimal"
              @intent="default"
              @size="sm"
              class="!p-2 !rounded-md"
              @isActive={{this.isDrawerActive "course-details"}}
              data-test-button="footer-course-details-button"
              {{on "click" (fn this.handleDrawerClick "course-details")}}
            >
              <PhStudent @size="20" class="md:hidden" />
              <PhStudent @size="24" class="hidden md:block" />
            </TooltipButton>

            <TooltipButton
              @tooltip={{t "tuition_assistance.application_approval.partner_information"}}
              @tooltipPlacement="top"
              @appearance="minimal"
              @intent="default"
              @size="sm"
              class="!p-2 !rounded-md"
              @isActive={{this.isDrawerActive "employer-info"}}
              data-test-button="footer-partner-info-button"
              {{on "click" (fn this.handleDrawerClick "employer-info")}}
            >
              <PhBuilding @size="20" class="md:hidden" />
              <PhBuilding @size="24" class="hidden md:block" />
            </TooltipButton>

            <TooltipButton
              @tooltip={{t "tuition_assistance.application_approval.financial_details"}}
              @tooltipPlacement="top"
              @appearance="minimal"
              @intent="default"
              @size="sm"
              class="!p-2 !rounded-md"
              @isActive={{this.isDrawerActive "financial-details"}}
              data-test-button="footer-financial-details-button"
              {{on "click" (fn this.handleDrawerClick "financial-details")}}
            >
              <PhCurrencyCircleDollar @size="20" class="md:hidden" />
              <PhCurrencyCircleDollar @size="24" class="hidden md:block" />
            </TooltipButton>

            <TooltipButton
              @tooltip="Attachments"
              @tooltipPlacement="top"
              @appearance="minimal"
              @intent="default"
              @size="sm"
              class="!p-2 !rounded-md"
              @isActive={{this.isDrawerActive "attachments"}}
              data-test-button="footer-attachments-button"
              {{on "click" (fn this.handleDrawerClick "attachments")}}
            >
              <PhFile @size="20" class="md:hidden" />
              <PhFile @size="24" class="hidden md:block" />
            </TooltipButton>

            <TooltipButton
              @tooltip={{t "tuition_assistance.application_approval.timeline"}}
              @tooltipPlacement="top"
              @appearance="minimal"
              @intent="default"
              @size="sm"
              class="!p-2 !rounded-md"
              @isActive={{this.isDrawerActive "timeline"}}
              data-test-button="footer-timeline-button"
              {{on "click" (fn this.handleDrawerClick "timeline")}}
            >
              <PhCalendarDots @size="20" class="md:hidden" />
              <PhCalendarDots @size="24" class="hidden md:block" />
            </TooltipButton>

            {{#if this.showCourseCash}}
              <TooltipButton
                @tooltip={{this.courseCashTooltip}}
                @tooltipPlacement="top"
                @appearance="minimal"
                @intent="default"
                @size="sm"
                class="!p-2 !rounded-md"
                @isActive={{this.isDrawerActive "course-cash"}}
                data-test-button="footer-course-cash-button"
                {{on "click" this.handleCourseCashClick}}
              >
                <PhCreditCard @size="20" class="md:hidden" />
                <PhCreditCard @size="24" class="hidden md:block" />
              </TooltipButton>
            {{/if}}

            {{! More Actions Menu - Popover with Listbox on desktop }}
            <div class="hidden md:block md:mr-16">
              <Popover
                @placement="top-end"
                @isOpen={{this.isMoreActionsPopoverOpen}}
                @onOpen={{this.openMoreActionsPopover}}
                @onClose={{this.closeMoreActionsPopover}}
              >
                <:trigger as |pop|>
                  <Button
                    @appearance="minimal"
                    @intent="default"
                    @size="sm"
                    class="!p-2 !rounded-md hover:tio-bg-brand-soft transition-colors"
                    {{pop.register}}
                    {{on "click" this.openMoreActionsPopover}}
                    data-test-footer-more-actions-button
                  >
                    <PhDotsThree @size="24" class="tio-fg-secondary hover:tio-fg-brand" />
                  </Button>
                </:trigger>

                <:body>
                  <Listbox
                    @intent="primary"
                    @onAction={{this.handleMoreActionsMenu}}
                    @disabledKeys={{this.disabledMenuKeys}}
                    as |l|
                  >
                    <l.Item @key="submitForUser" data-test-footer-submit-for-user-button>
                      <span class="tio-row-tight">
                        <PhHandDeposit @size="16" />
                        Submit For User
                      </span>
                    </l.Item>

                    <l.Separator />

                    <l.Item @key="reassign" data-test-footer-reassign-button>
                      <span class="tio-row-tight">
                        <PhUserSwitch @size="16" />
                        {{t "tuition_assistance.approval_queue.approval_status"}}
                      </span>
                    </l.Item>

                    <l.Item @key="rescind" data-test-footer-rescind-button>
                      <span class="tio-row-tight">
                        <PhArrowUUpLeft @size="16" />
                        Undo Last
                      </span>
                    </l.Item>

                    <l.Separator />

                    {{! Show Reopen if rejected, otherwise show Reject }}
                    {{#if this.isRejected}}
                      <l.Item @key="reopen" @intent="primary" data-test-footer-reopen-button>
                        <span class="tio-row-tight">
                          <PhClockCounterClockwise @size="16" />
                          Reopen Application
                        </span>
                      </l.Item>
                    {{else}}
                      <l.Item @key="reject" @intent="danger" data-test-footer-reject-button>
                        <span class="tio-row-tight">
                          <PhXCircle @size="16" />
                          {{t "tuition_assistance.application_details.reject_application"}}
                        </span>
                      </l.Item>
                    {{/if}}

                    {{#if this.features.isAdminApp}}
                      <l.Separator />
                      <l.Item
                        @key="policyOverride"
                        data-test-footer-policy-override-button
                        class="hover:!tio-fg-danger hover:!tio-bg-danger-soft"
                      >
                        <span class="tio-row-tight">
                          <PhShieldWarning @size="16" />
                          {{t
                            "common.tuition_assistance.application_details.policy_override_prompt"
                          }}
                        </span>
                      </l.Item>
                    {{/if}}

                    {{#if this.showInCollections}}
                      <l.Separator />
                      <l.Item @key="inCollections" data-test-footer-in-collections-button>
                        <span class="tio-row-tight">
                          <PhTag @size="16" />
                          {{if
                            this.isInCollections
                            (t
                              "tuition_assistance.application_details.in_collections_toggle.remove_title"
                            )
                            (t
                              "tuition_assistance.application_details.in_collections_toggle.mark_title"
                            )
                          }}
                        </span>
                      </l.Item>
                    {{/if}}
                  </Listbox>
                </:body>
              </Popover>
            </div>

            {{! More Actions - simple button for mobile }}
            <Button
              @appearance="minimal"
              @intent="default"
              @size="sm"
              class="md:hidden !p-2 !rounded-md hover:tio-bg-brand-soft transition-colors justify-center"
              @isDisabled={{not (or this.canRejectApplication this.isRejected)}}
              {{on "click" (if this.isRejected this.openReopenModal this.openRejectModal)}}
              data-test-footer-more-actions-button-mobile
            >
              <PhDotsThree @size="20" class="tio-fg-secondary hover:tio-fg-brand" />
            </Button>
          </div>
        </div>
      </div>
    </div>

    {{! Rejection Modal }}
    <Modal
      @isOpen={{this.isRejectModalOpen}}
      @onClose={{this.closeRejectModal}}
      data-test-reject-modal
      @ariaLabel={{t "common.aria_labels.reject_application"}}
    >
      <:header> {{t "tuition_assistance.application_details.reject_application"}}</:header>
      <:body>
        {{#if @application.canRepayOnReject}}
          <AlertBanner @intent="warning" class="my-4">
            <p data-test-reject-repayment-alert>
              {{t
                "tuition_assistance.application_details.reject_funds_outstanding"
                htmlSafe=true
                courseCash=(t "common.tas.course_cash.default" htmlSafe=true)
              }}
            </p>
          </AlertBanner>

          <Checkbox
            @label={{t "tuition_assistance.application_details.reject_request_repayment"}}
            @checked={{this.requestRepaymentOnReject}}
            @onChange={{this.toggleRequestRepaymentOnReject}}
            class="tio-row-flush tio-text-sm-semibold"
            data-test-reject-request-repayment
          />

          {{#if this.requestRepaymentOnReject}}
            <div class="tio-col mb-4 mt-2">
              {{#if this.showRepaymentAmountOptions}}
                <div class="tio-text-sm-semibold tio-fg-primary">
                  {{t "tuition_assistance.application_details.reject_select_amount"}}
                </div>

                {{#each this.repaymentAmountOptions as |option|}}
                  <SelectableCard
                    @value={{option.key}}
                    @label={{concat option.label ": " (formatAsDollars option.amount)}}
                    @description={{option.description}}
                    @isSelected={{eq this.repaymentAmountSelection option.key}}
                    @onChange={{this.selectRepaymentAmount}}
                  />
                {{/each}}

                <div class="text-center tio-fg-disabled text-sm">
                  {{t "tuition_assistance.application_details.reject_or_custom_amount"}}
                </div>
              {{/if}}

              <Input
                @label={{t "tuition_assistance.application_details.reject_repayment_amount"}}
                @value={{this.requestedRepaymentAmountDisplay}}
                @onChange={{this.updateRequestedRepaymentAmount}}
                @classes={{hash base="tio-text-xs-semibold"}}
                placeholder="$00.00"
                data-test-reject-repayment-amount
                {{inputmask alias="currency" prefix="$" unmaskAsNumber=true digits="2"}}
              />
            </div>
          {{/if}}

          {{#if this.canHoldOnReject}}
            <Checkbox
              @label={{t "tuition_assistance.application_details.reject_hold_employee"}}
              @checked={{this.holdEmployeeOnReject}}
              @onChange={{this.toggleHoldEmployeeOnReject}}
              class="tio-row-flush tio-text-sm-semibold mb-4"
              data-test-reject-hold-employee
            />
          {{/if}}
        {{/if}}
        {{#if this.rejectTextAreaOptions.length}}
          <SelectTextArea
            @options={{this.rejectTextAreaOptions}}
            @optionLabel="title"
            @optionValue="body"
            @value={{this.rejectNote}}
            @onChange={{this.updateRejectNote}}
            @placeholder="Select a response or enter custom feedback..."
            @multiSelect={{true}}
          />
        {{else}}
          <Textarea
            @value={{this.rejectNote}}
            @onChange={{this.updateRejectNote}}
            placeholder="Enter feedback for the employee..."
            data-test-reject-note
          />
        {{/if}}
        {{#if this.rejectError}}
          <ErrorMessages
            @showErrors={{true}}
            @error={{this.rejectError}}
            class="tio-row-tight text-sm"
          />
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.confirmRejectionTask.isRunning}}
          @submitText="Submit"
          @onSubmit={{this.confirmRejectionTask.perform}}
          @onCancel={{this.closeRejectModal}}
          @submitDisabled={{not this.isRejectFormValid}}
        />
      </:footer>
    </Modal>

    {{! Submit for User Modal }}
    <Modal
      @isOpen={{this.submitForUserModalOpen}}
      @onClose={{this.closeSubmitForUserModal}}
      data-test-submit-for-user-modal
      @ariaLabel={{t "common.aria_labels.submit_application_for_user"}}
    >
      <:header>
        {{t "tuition_assistance.application_approval.submit_for_user.title"}}
      </:header>
      <:body>
        <div class="tio-col-loose">
          <p class="text-sm tio-fg-secondary">
            {{t "tuition_assistance.application_approval.submit_for_user.description"}}
          </p>

          {{#if
            (and
              (eq @application.state "TAS.ApplicationState.DEFINE_COURSES")
              @application.tasProgramInstance.tasProgramTemplate.isDirectPayProgram
            )
          }}
            <AlertBanner @intent="info">
              {{#if this.isCourseCashPayment}}
                {{t "tuition_assistance.application_approval.submit_for_user.course_cash_info"}}
              {{else}}
                {{t "tuition_assistance.application_approval.submit_for_user.reimbursement_info"}}
              {{/if}}
            </AlertBanner>

            {{#if this.isDraftCourseCashWithoutCardholder}}
              <AlertBanner @intent="warning">
                {{t "tuition_assistance.application_approval.submit_for_user.missing_cardholder"}}
              </AlertBanner>
            {{/if}}
          {{/if}}

          {{#if this.missingRequirementsForSubmitForUser.length}}
            <ErrorMessages
              @showErrors={{true}}
              @error={{this.missingRecsAlertForSubmitForUser}}
              class="tio-row-tight text-sm"
            />
          {{/if}}

          {{#if this.submitForUserError}}
            <ErrorMessages
              @showErrors={{true}}
              @error={{this.submitForUserError}}
              class="tio-row-tight text-sm"
            />
          {{/if}}
        </div>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.submitForUserTask.isRunning}}
          @submitText={{t "common.submit"}}
          @cancelText={{t "common.cancel"}}
          @onSubmit={{this.submitForUserTask.perform}}
          @onCancel={{this.closeSubmitForUserModal}}
          @submitDisabled={{or
            (gt this.missingRequirementsForSubmitForUser.length 0)
            this.isDraftCourseCashWithoutCardholder
          }}
        />
      </:footer>
    </Modal>

    {{! Rescind Modal }}
    <Modal
      @isOpen={{this.isRescindModalOpen}}
      @onClose={{this.closeRescindModal}}
      data-test-rescind-modal
      @ariaLabel={{t "common.aria_labels.rescind"}}
    >
      <:header>
        Rescind
      </:header>
      <:body>
        <p class="tio-text-heading tio-fg-primary">
          {{t "tuition_assistance.application_details.rescind.body"}}
        </p>
        <p class="my-4 tio-fg-primary">
          {{t "tuition_assistance.application_details.rescind.footnote"}}
        </p>

        {{#if this.rescindError}}
          <ErrorMessages
            @showErrors={{true}}
            @error={{this.rescindError}}
            class="tio-row-tight text-sm"
          />
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.rescindStateAction.isRunning}}
          @submitText="Confirm"
          @onSubmit={{this.rescindStateAction.perform}}
          @onCancel={{this.closeRescindModal}}
        />
      </:footer>
    </Modal>

    {{! Request Change Modal }}
    <Modal
      @isOpen={{this.isRequestChangeModalOpen}}
      @onClose={{this.closeRequestChangeModal}}
      data-test-request-change-modal
      @ariaLabel={{t "common.aria_labels.request_change"}}
    >
      <:header>
        Request Change
      </:header>
      <:body>
        <p class="text-sm tio-fg-secondary mb-4">
          Send this application back to the employee with feedback for revision.
        </p>

        {{#if this.requestChangeTextAreaOptions.length}}
          <SelectTextArea
            @options={{this.requestChangeTextAreaOptions}}
            @optionLabel="title"
            @optionValue="body"
            @value={{this.requestChangeNote}}
            @onChange={{this.updateRequestChangeNote}}
            @placeholder="Select a response or enter custom feedback..."
            @multiSelect={{true}}
          />
        {{else}}
          <Textarea
            @value={{this.requestChangeNote}}
            @onChange={{this.updateRequestChangeNote}}
            placeholder="Enter feedback for the employee..."
            data-test-request-change-note
            class="min-h-32"
          />
        {{/if}}

        {{#unless this.requestChangeNote}}
          <p class="text-sm tio-fg-danger mt-2">
            A comment is required before submitting.
          </p>
        {{/unless}}

        {{#if this.requestChangeError}}
          <ErrorMessages
            @showErrors={{true}}
            @error={{this.requestChangeError}}
            class="tio-row-tight text-sm mt-2"
          />
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.confirmRequestChangeTask.isRunning}}
          @submitText="Submit"
          @onSubmit={{this.confirmRequestChangeTask.perform}}
          @onCancel={{this.closeRequestChangeModal}}
          @submitDisabled={{not this.requestChangeNote}}
        />
      </:footer>
    </Modal>

    {{! Reopen Modal }}
    <Modal
      @isOpen={{this.isReopenModalOpen}}
      @onClose={{this.closeReopenModal}}
      @size="md"
      data-test-reopen-modal
      @ariaLabel={{t "common.aria_labels.reopen_application"}}
    >
      <:header>
        Reopen Application
      </:header>
      <:body>
        <p class="tio-fg-secondary mb-4">
          Are you sure you want to reopen this rejected application? This will return the
          application to a pending state for review.
        </p>

        {{#if this.reopenError}}
          <ErrorMessages @showErrors={{true}} @error={{this.reopenError}} class="mt-4" />
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.reopenApplication.isRunning}}
          @submitText="Reopen Application"
          @cancelText="Cancel"
          @onSubmit={{this.reopenApplication.perform}}
          @onCancel={{this.closeReopenModal}}
          @submitIntent="primary"
          @submitDisabled={{this.reopenApplication.isRunning}}
        />
      </:footer>
    </Modal>

    {{! Approve Modal - Unmount/remount to reset state }}
    {{#if this.isApproveModalOpen}}
      <ApproveModal
        @show={{this.isApproveModalOpen}}
        @onClose={{this.closeApproveModal}}
        @application={{@application}}
        @draftComment={{this.approveCommentDraft}}
        @onDraftCommentChange={{this.updateApproveCommentDraft}}
      />
    {{/if}}

    {{! Policy Override Modal }}
    <Modal
      @isOpen={{this.isOverrideModalOpen}}
      @onClose={{this.closeOverrideModal}}
      data-test-policy-override-modal
      @ariaLabel={{t "common.aria_labels.override_policies"}}
    >
      <:header>
        {{t "common.tuition_assistance.application_details.policy_override_confirm"}}
      </:header>
      <:body>
        <p class="tio-fg-secondary mb-4">
          {{t "common.tuition_assistance.application_details.policy_override_description"}}
        </p>
        <p class="font-semibold italic tio-fg-secondary">
          {{t "common.tuition_assistance.application_details.policy_override_warning"}}
        </p>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.confirmPolicyOverrideTask.isRunning}}
          @onCancel={{this.closeOverrideModal}}
          @onSubmit={{this.confirmPolicyOverrideTask.perform}}
          @submitText={{t "common.confirm"}}
          @submitIntent="danger"
        />
      </:footer>
    </Modal>

    {{! In Collections Modal }}
    <Modal
      @isOpen={{this.isInCollectionsModalOpen}}
      @onClose={{this.closeInCollectionsModal}}
      data-test-in-collections-modal
      @ariaLabel={{if
        this.isInCollections
        (t "tuition_assistance.application_details.in_collections_toggle.remove_title")
        (t "tuition_assistance.application_details.in_collections_toggle.mark_title")
      }}
    >
      <:header>
        {{if
          this.isInCollections
          (t "tuition_assistance.application_details.in_collections_toggle.remove_title")
          (t "tuition_assistance.application_details.in_collections_toggle.mark_title")
        }}
      </:header>
      <:body>
        <p class="tio-fg-secondary mb-4">
          {{if
            this.isInCollections
            (t "tuition_assistance.application_details.in_collections_toggle.remove_body")
            (t "tuition_assistance.application_details.in_collections_toggle.mark_body")
          }}
        </p>

        {{#if this.inCollectionsError}}
          <ErrorMessages
            @showErrors={{true}}
            @error={{this.inCollectionsError}}
            class="tio-row-tight text-sm"
          />
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.toggleInCollectionsTask.isRunning}}
          @onCancel={{this.closeInCollectionsModal}}
          @onSubmit={{this.toggleInCollectionsTask.perform}}
          @submitText={{t "common.confirm"}}
        />
      </:footer>
    </Modal>
  </template>
}

export default ApprovalFooter;
